import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import ChangeStatus from '../../Dashboard/DashboardTable/ChangeStatus';
import PayaPayment from '../../Healthboard/Components/PayaPayment';
import LoaderInPopup from '../../Ui/LoaderInPopup';
import MobileAppModal from '../../Ui/MobileAppModal';
import TextMessage from '../../Ui/TextMessage';
import Report from '../../Ui/Report';
import Popup from '../Popup';
import MfaNotice from '../../MfaNoticeDialog';
import { isDesktop } from '../../../utils/mobileHelpers';
import PayaAccountForm from '../../Healthboard/Components/PayaAccountForm';
import PayaPaymentHandleResponse from '../../Healthboard/Components/PayaPaymentHandleResponse';
import Refills from '../../PatientHealthboard/PatientPortal/Refills';

import { AUTH, COMMON, REPORT } from '../../../constants/strings';
import {
  REPORT_ENDPOINT_BY_RESOURCE_TYPE,
  UI_POPUP_TYPES,
} from '../../../constants/props';
import { enhance } from './enhance';

const AppPopup = ({
  formatMessage,
  sendReport,
  contentType,
  contentOptions,
  visible,
  hidePopup,
}) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!visible) setData(null);
  }, [visible]);

  const handleReportSubmit = () => {
    const { resource, id: reportableId } = contentOptions;

    const payload = {
      reportableId,
      reportType: REPORT_ENDPOINT_BY_RESOURCE_TYPE[resource],
      reasonId: data,
    };

    sendReport(payload);
    hidePopup();
  };

  const getComponent = () => {
    switch (contentType) {
      case UI_POPUP_TYPES.REPORT:
        return <Report options={contentOptions} onSelect={setData} />;

      case UI_POPUP_TYPES.MESSAGE:
        return (
          <TextMessage
            icon={contentOptions.icon}
            title={contentOptions.title}
            message={contentOptions.message}
          />
        );

      case UI_POPUP_TYPES.MOBILE_APP:
        return <MobileAppModal />;

      case UI_POPUP_TYPES.DASHBOARD_CHANGE_STATUS:
        return <ChangeStatus options={contentOptions} />;

      case UI_POPUP_TYPES.PAYA_PAYMENT:
        return <PayaPayment options={contentOptions} onClose={hidePopup} />;

      case UI_POPUP_TYPES.LOADER:
        return <LoaderInPopup />;

      case UI_POPUP_TYPES.MFA_NOTICE:
        return <MfaNotice />;

      case UI_POPUP_TYPES.PAYA_ACCOUNT_FORM:
        return <PayaAccountForm onClose={hidePopup} options={contentOptions} />;

      case UI_POPUP_TYPES.PAYA_PAYMENT_HANDLE_RESPONSE:
        return (
          <PayaPaymentHandleResponse
            onClose={hidePopup}
            options={{ ...contentOptions, formatMessage }}
          />
        );

      case UI_POPUP_TYPES.MEDICATIONS_REFILLS:
        return (
          <Refills
            onClose={hidePopup}
            options={{ ...contentOptions, formatMessage }}
          />
        );

      default:
        return null;
    }
  };

  const getOptions = () => {
    switch (contentType) {
      case UI_POPUP_TYPES.REPORT:
        return {
          onClose: hidePopup,
          title: formatMessage(REPORT.title),
          width: isDesktop() ? 'xsmall' : 'medium',
          customControls: true,
          onSubmit: handleReportSubmit,
          submitText: formatMessage(COMMON.send),
          enabled: Boolean(data),
        };

      case UI_POPUP_TYPES.MESSAGE: {
        const closeModal = () => {
          if (contentOptions.popupClose) contentOptions.popupClose();
          hidePopup();
        };
        const submitModal = () => {
          if (contentOptions.popupAction) contentOptions.popupAction();
          hidePopup();
        };

        return {
          enabled: true,
          footerCentered: true,
          buttonWidth: 150,
          submitText: formatMessage(AUTH.submit.expiredToken),
          mobileFullScreen: false,
          white: true,
          width: 'rubber',
          withHeading: false,
          customControls: true,
          onSubmit: submitModal,
          onClose: closeModal,
          strictClose: true,
        };
      }

      case UI_POPUP_TYPES.MOBILE_APP:
        return {
          onClose: hidePopup,
          withHeading: false,
          mobileFullScreen: false,
          centered: true,
          contentNoPadding: true,
          width: 'rubber',
        };

      case UI_POPUP_TYPES.DASHBOARD_CHANGE_STATUS:
        return {
          onClose: hidePopup,
          title: formatMessage({ id: 'dashboard.record.updateVisitDetails' }),
          width: isDesktop() ? 'xsmall' : 'medium',
        };

      case UI_POPUP_TYPES.PAYA_PAYMENT: {
        return {
          onClose: hidePopup,
          strictClose: true,
          withHeading: false,
          overflowVisible: true,
          contentNoPadding: true,
          autoHeight: true,
        };
      }

      case UI_POPUP_TYPES.LOADER: {
        return {
          withHeading: false,
          contentNoPadding: true,
          overflowVisible: true,
          autoHeight: true,
        };
      }

      case UI_POPUP_TYPES.MFA_NOTICE: {
        return {
          withHeading: false,
          width: 'rubber',
          centered: true,
          onClose: hidePopup,
          mobileFullScreen: false,
        };
      }

      case UI_POPUP_TYPES.PAYA_ACCOUNT_FORM: {
        return {
          onClose: hidePopup,
          strictClose: true,
          withHeading: false,
          overflowVisible: true,
          contentNoPadding: true,
          autoHeight: true,
        };
      }

      case UI_POPUP_TYPES.PAYA_PAYMENT_HANDLE_RESPONSE: {
        return {
          withHeading: false,
          width: 'rubber',
          onClose: hidePopup,
          mobileFullScreen: false,
        };
      }

      case UI_POPUP_TYPES.MEDICATIONS_REFILLS: {
        return {
          withHeading: false,
          overflowVisible: true,
          contentNoPadding: false,
          autoHeight: true,
          mobileFullScreen: false,
          disableFillBackground: true,
        };
      }

      default:
        return {};
    }
  };

  const options = getOptions();
  const component = getComponent();

  if (!component) return null;
  return <Popup opened={visible} {...options} children={component} />;
};

AppPopup.propTypes = {
  sendReport: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
  contentType: PropTypes.string.isRequired,
  contentOptions: PropTypes.object.isRequired,
  visible: PropTypes.bool.isRequired,
  hidePopup: PropTypes.func.isRequired,
};

export default enhance(AppPopup);



// WEBPACK FOOTER //
// ./src/components/common/AppPopup/index.jsx